import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@platform/core/lib/cx';
import { useHistoryQuery } from '@platform/state/api';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { Spinner } from '@platform/ui';
import { kindFieldOf } from '../form/header/kindFieldOf';
import { saveFile } from '../list/saveFile';
import { historyCsv, historyGroupOf } from './history/historyKinds';
import { HistoryRow } from './history/HistoryRow';
import { maskHistory } from './history/maskHistory';
import { useHistoryText } from './history/useHistoryText';
import { useHistoryValue } from './history/useHistoryValue';
import type { IHistoryTabProps } from './IHistoryTabProps';

const FILTERS = ['', 'comment', 'change', 'link'] as const;
const LABELS: Record<string, string> = { '': 'all', comment: 'comments', change: 'changes', link: 'links' };

/**
 * Вкладка «Історія» (UIUX forms/tabs, прототип v4): фільтри-пігулки «Усі · N · Коментарі · N · Зміни · N · Зв’язки · N» і «Експорт
 * історії» (CSV); стрічка подій — хто, що, було → стало, коли. Значення полів з персональними даними, прихованих від користувача, не
 * показуються й не експортуються (maskHistory).
 */
export function HistoryTab({ ws, rt, record }: IHistoryTabProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useHistoryQuery({ recordId: record.id });
  const strings = useModuleStrings();
  const kindField = rt ? kindFieldOf(rt) : undefined;
  const kindValue = kindField ? record.values[kindField.key] : undefined;
  // Тип у події створення (прототип recType): вид запису (бізнес-аналіз), інакше однина типу — з малої
  const typeName = !rt ? '' : (kindField && typeof kindValue === 'string' && kindValue
    ? strings.option(rt.key, kindField.key, kindValue, kindField.options?.find((o) => o.value === kindValue)?.label ?? kindValue)
    : strings.entity(rt.key, 'singular', rt.singular ?? rt.name)).toLocaleLowerCase();
  const { lead, detail } = useHistoryText(typeName);
  const valueText = useHistoryValue(ws, rt);
  const [kind, setKind] = useState('');
  const all = useMemo(() => maskHistory(data ?? [], record.permissions?.restrictedFields ?? []), [data, record.permissions]);
  const shown = all.filter((h) => !kind || historyGroupOf(h) === kind);
  if (isLoading) return <Spinner />;
  const exportCsv = () => saveFile(new Blob([historyCsv(shown, t('recordPage.history.csvHead'), (h) => t(`recordPage.history.kinds.${historyGroupOf(h)}`) + ` · ${lead(h)}`, detail)], { type: 'text/csv' }), `${record.display}-history.csv`);
  return (
    <div className="rtab-history">
      <div className="rtab-bar rtab-bar--tight" role="toolbar" aria-label={t('recordPage.history.filters')}>
        {FILTERS.map((k) => (
          <button key={k} type="button" className={cx('rtab-chipbtn', kind === k && 'is-on')} aria-pressed={kind === k} onClick={() => setKind(k)}>
            {t(`recordPage.history.${LABELS[k]}`)} · {k ? all.filter((h) => historyGroupOf(h) === k).length : all.length}
          </button>
        ))}
        <span className="rtab-bar__spacer" />
        <button type="button" className="rtab-btn rtab-btn--sm" disabled={!shown.length} onClick={exportCsv}>{t('recordPage.history.export')}</button>
      </div>
      <div className="rtab-hist__list">
        {shown.map((h) => <HistoryRow key={h.id} entry={h} valueText={valueText} typeName={typeName} />)}
        {!shown.length && <p className="rtab-empty">{all.length ? t('recordPage.history.noMatch') : t('recordPage.history.empty')}</p>}
      </div>
    </div>
  );
}
